import { createContext, useState, useEffect, type ReactNode } from 'react';
import { onIdTokenChanged, type User } from 'firebase/auth';
import { getAuthInstance } from './firebase-init';
import { getClaims } from './auth-methods';
import * as authMethods from './auth-methods';
import type { AuthContextValue, AuthState, AuthUser } from './types';

export const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthUser(user: User | null): AuthUser | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    claims: null,
    isLoading: true,
    error: null,
  });

  // Subscribe to token changes. onIdTokenChanged (vs onAuthStateChanged) also
  // fires on token refresh, so updated custom claims propagate into state and a
  // revoked/expired token surfaces as a sign-out.
  useEffect(() => {
    const auth = getAuthInstance();

    const unsubscribe = onIdTokenChanged(
      auth,
      async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const claims = await getClaims(firebaseUser);
            setState({
              user: toAuthUser(firebaseUser),
              claims,
              isLoading: false,
              error: null,
            });
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'An error occurred';
            setState({
              user: toAuthUser(firebaseUser),
              claims: null,
              isLoading: false,
              error: message,
            });
          }
        } else {
          setState({
            user: null,
            claims: null,
            isLoading: false,
            error: null,
          });
        }
      },
      (error) => {
        setState({
          user: null,
          claims: null,
          isLoading: false,
          error: error.message,
        });
      },
    );

    return () => unsubscribe();
  }, []);

  // Force sign-out when an admin changes this user's claims. `set-claims`
  // revokes the user's refresh tokens; a forced token refresh then fails, so we
  // sign the user out and require a fresh login (which mints a token with the
  // new roles). Checked on window focus and on an interval to bound the delay.
  useEffect(() => {
    const auth = getAuthInstance();
    let cancelled = false;

    const checkRevocation = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        // Forces the SDK to exchange the refresh token; throws if it was revoked.
        await user.getIdToken(true);
      } catch {
        if (!cancelled) {
          await authMethods.signOut().catch(() => {});
        }
      }
    };

    const onFocus = () => void checkRevocation();
    window.addEventListener('focus', onFocus);
    const interval = window.setInterval(() => void checkRevocation(), 5 * 60 * 1000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await authMethods.signInWithEmailPassword(email, password);
      // onAuthStateChanged will update state
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw error;
    }
  };

  const signOutHandler = async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await authMethods.signOut();
      // onAuthStateChanged will update state
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw error;
    }
  };

  const refreshClaims = async () => {
    const auth = getAuthInstance();
    const user = auth.currentUser;
    if (user) {
      const claims = await getClaims(user);
      setState((prev) => ({ ...prev, claims }));
    }
  };

  const value: AuthContextValue = {
    ...state,
    signIn,
    signOut: signOutHandler,
    getIdToken: authMethods.getIdToken,
    refreshClaims,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
